import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleOutline as ChatBubbleIcon } from '@mui/icons-material';
import { ChatMessageDownloadButton, ExternalLinkButton, NameNicknameDisplay, TimeDisplay } from '../common';
import { Patient } from '../../types/patient';
import { NURSE_TYPE_THEME, ROLE_COLORS, ROLE_TYPES } from '../../types/nurse';
import { getExternalAdminTalkRoomUrl } from '../../utils/urlUtils';
import { MESSAGE_DISPLAY_LIMIT } from '../../types/common';

interface AdminNursesSectionProps {
  patient: Patient;
}

const AdminNursesSection: React.FC<AdminNursesSectionProps> = ({ patient }) => {
  const navigate = useNavigate();
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // 患者情報から運営相談ルーム一覧を取得
  const adminTalkRooms = patient.admin_talk_rooms || [];
  useEffect(() => {
    // 初期設定
    setLoading(false);
    setError(adminTalkRooms.length === 0 ? '運営相談ルームはありません' : null);
  }, [ adminTalkRooms.length ]);
  // データがない場合の表示
  if (!loading && (adminTalkRooms.length === 0 || error)) {
    return (
      <Card
        sx={{
          mb: 4,
          border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
          boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
        }}>
        <CardContent>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>運営相談ルーム</Typography>
              <Divider sx={{ mb: 2 }}/>

              <Box sx={{ p: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                  {error || '運営相談ルームはありません'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid ${NURSE_TYPE_THEME.COMMON.BORDER}40`,
        boxShadow: `0 1px 4px ${NURSE_TYPE_THEME.COMMON.BACKGROUND}30`
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>運営相談ルーム</Typography>
            <Divider sx={{ mb: 2 }}/>

            {loading ? (
              <Box sx={{ my: 2 }}>
                <LinearProgress/>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  データを読み込み中...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '20px',
                    background: `linear-gradient(to top, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}CC, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}00)`,
                    pointerEvents: 'none',
                    zIndex: 1,
                    display: 'block'
                  }
                }}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 1,
                    maxHeight: '500px', // 最大高さを指定
                    overflow: 'auto', // スクロール可能に
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}`,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: `${NURSE_TYPE_THEME.NORMAL.DARK}`
                      }
                    }
                  }}
                >
                  <Table
                    stickyHeader
                    aria-label="運営相談ルーム一覧"
                    sx={{
                      "& .MuiTableHead-root": {
                        position: "sticky",
                        top: 0,
                        zIndex: 2
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND} !important`,
                        borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                      }
                    }}
                  >
                    <TableHead
                      sx={{ backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND, borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}` }}>
                      <TableRow>
                        <TableCell width="7%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          ロール
                        </TableCell>
                        <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          名前 / ニックネーム
                        </TableCell>
                        <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          表示状態
                        </TableCell>
                        <TableCell width="15%">
                          {/*スペーサー*/}
                        </TableCell>
                        <TableCell width="30%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          最終メッセージ / 最終更新
                        </TableCell>
                        <TableCell width="13%" sx={{ fontWeight: 'medium', color: 'text.secondary' }} align="left">
                          チャット
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminTalkRooms
                      .slice() // 元の配列を変更しないようにコピー
                      .sort((a, b) => {
                        // last_chat_send_atがない場合は後ろに配置
                        if (!a.last_chat_send_at) return 1;
                        if (!b.last_chat_send_at) return -1;
                        // 降順（新しい順）に並べる
                        return new Date(b.last_chat_send_at).getTime() - new Date(a.last_chat_send_at).getTime();
                      })
                      .map((room) => (
                        <TableRow
                          key={room.id}
                          hover
                          onClick={() => navigate(`/nurses/${room.parent.id}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: ROLE_COLORS[room.parent.role_type as keyof typeof ROLE_COLORS] || '#757575',
                                fontSize: '0.75rem',
                                borderLeft: `3px solid ${ROLE_COLORS[room.parent.role_type as keyof typeof ROLE_COLORS] || '#757575'}`,
                                pl: 1,
                                py: 0.3,
                                opacity: 0.85,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {ROLE_TYPES[room.parent.role_type as keyof typeof ROLE_TYPES] || room.parent.role_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <NameNicknameDisplay
                              name={room.parent.name}
                              nickname={room.parent.nickname}
                              nameFontWeight="bold"
                              nameColor="text.primary"
                              isTestAccount={(room.parent as any)?.is_test_account || false}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex' }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: room.can_display ? '#2e7d32' : room.can_display === false ? '#c62828' : '#757575',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  mr: 0.5
                                }}
                              >
                                {room.can_display ? '○' : room.can_display === false ? '×' : '-'}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: room.can_display ? '#2e7d32' : room.can_display === false ? '#c62828' : '#757575',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {room.can_display ? '表示中' : room.can_display === false ? '非表示' : '未設定'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {/*スペーサー*/}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {/* 最終メッセージ */}
                              {room.last_chat_message ? (
                                <Tooltip title={room.last_chat_message} placement="top-start">
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 'calc(100% - 24px)'
                                      }}
                                    >
                                      {room.last_chat_message.length > MESSAGE_DISPLAY_LIMIT
                                        ? `${room.last_chat_message.substring(0, MESSAGE_DISPLAY_LIMIT)}...`
                                        : room.last_chat_message
                                      }
                                    </Typography>
                                    {room.last_chat_message.length > MESSAGE_DISPLAY_LIMIT && (
                                      <ChatBubbleIcon
                                        fontSize="small"
                                        sx={{
                                          ml: 0.5,
                                          color: 'text.secondary',
                                          opacity: 0.7,
                                          fontSize: '1rem'
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                  メッセージはありません
                                </Typography>
                              )}

                              {/* 最終更新日時 */}
                              {room.last_chat_send_at && (
                                <TimeDisplay
                                  timestamp={room.last_chat_send_at}
                                  prefix="最終送信"
                                  fontSize="0.8rem"
                                  tooltipPlacement="left"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <ExternalLinkButton
                                href={getExternalAdminTalkRoomUrl(room.id)}
                                label="開く"
                                tooltip="運営相談ルームを開く"
                                tooltipPlacement="left"
                                size="small"
                                variant="outlined"
                                buttonType="chat"
                                themeColor={{
                                  main: NURSE_TYPE_THEME.NORMAL.DARK,
                                  hover: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}99`
                                }}
                                sx={{ minWidth: 'auto', px: 1 }}
                              />
                              <div onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}>
                                <ChatMessageDownloadButton
                                  roomType="ADMIN_TALK_ROOM"
                                  roomId={room.id}
                                  size="small"
                                  variant="outlined"
                                  iconOnly={true}
                                  themeColor={{
                                    main: NURSE_TYPE_THEME.NORMAL.DARK,
                                    hover: NURSE_TYPE_THEME.NORMAL.BACKGROUND
                                  }}
                                />
                              </div>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default AdminNursesSection;
